import React from 'react';
import { CustomDropdown } from '../common/index.js';
import { Table } from '../table/index.js';
import { ConsultationsReadyForReviewChart, ConsultationProcessingTimesChart, DocumentActivityChart, UserActivityOverTimeChart, UserActivityByStaffChart, formatSnapshotTimestamp } from './ChartComponents.js';
import { UserActionLabels } from './userActionEnum.js';
import MetricCard from './MetricCard.js';
import TableSectionHeader from './TableSectionHeader.js';
import { AggregationModeDropdown, ChartDownloadButton, ChartHeader, chartFileSlug } from './ChartCardControls.js';
import { buildConsultationProcessingPoints, buildUserActivityOverTimeSeries } from './chartSeries.js';
import {
  downloadActivityOverTimeCSV,
  downloadConsultationProcessingCSV,
  downloadConsultationsReadyCSV,
  downloadUserActivityOverTimeCSV,
} from './insightsCsvExport.js';

const InsightsConsultationsSection = ({
  aggregationMode,
  averageConsultationTimeToReviewText,
  averageRecordingDurationText,
  byStaffDropdownOpen,
  combinedConsultationMetrics,
  combinedConsultationStaffSelection,
  consultationData,
  consultationProcessingTimeDropdown,
  consultationProcessingTimeOptions,
  consultationProcessingTimeSubtitles,
  consultationProcessingTimeXAxisLabels,
  consultationStaffAggregateData,
  consultationStaffTableColumns,
  consultationStaffTableDisabledFilters,
  consultationStaffTableFilterValues,
  consultationStaffTablePaginationState,
  consultationStaffTableSortState,
  consultationTeamAggregateData,
  consultationTeamTableColumns,
  consultationTeamTableDisabledFilters,
  consultationTeamTableFilterValues,
  consultationTeamTablePaginationState,
  consultationTeamTableSortState,
  consultationsReadyForReview,
  dateRangeText,
  focusedAction,
  focusedActionDropdown,
  getActionTitle,
  getExpandedUserIds,
  getUserSelectionText,
  handleDownloadStaffSpreadsheet,
  handleDownloadTeamSpreadsheet,
  handleFocusedActionChange,
  handleToggleAction,
  insightsType,
  reviewData,
  selectedActions,
  selectedConsultationTeamRows,
  setByStaffDropdownOpen,
  setConsultationStaffTableDisabledFilters,
  setConsultationStaffTableFilterValues,
  setConsultationStaffTablePaginationState,
  setConsultationStaffTableSortState,
  setConsultationTeamTableDisabledFilters,
  setConsultationTeamTableFilterValues,
  setConsultationTeamTablePaginationState,
  setConsultationTeamTableSortState,
  setSelectedConsultationStaffRows,
  setSelectedConsultationTeamRows,
  showUserCharts,
  staffSpreadsheetDisabled,
  teamSpreadsheetDisabled,
  totalEncounters,
  userActionOptions,
  userIdToName,
  viewMode
}) => {
  const filteredUserIds = getExpandedUserIds();
  const overTimeSeries = buildUserActivityOverTimeSeries({
    data: consultationData,
    selectedAction: focusedAction,
    userIdToName,
    filteredUserIds,
    mode: aggregationMode.mode,
  });
  const processingPoints = buildConsultationProcessingPoints(
    reviewData,
    consultationProcessingTimeDropdown.selected.value
  );

  return (
  <>
            {insightsType === 'consultations' && viewMode === 'gp-practice' && (
              <>
                <div className="insights-metrics">
                  <div className="metrics-group">
                    <MetricCard
                      value={totalEncounters}
                      label="Total encounters"
                      dateRange={dateRangeText}
                    />
                    <MetricCard
                      value={consultationsReadyForReview}
                      label="Consultations ready for review"
                      dateRange={formatSnapshotTimestamp(new Date().toISOString())}
                    />
                  </div>
                  <div className="metrics-group">
                    <MetricCard
                      value={averageRecordingDurationText}
                      label="Average recording duration"
                      dateRange={dateRangeText}
                    />
                    <MetricCard
                      value={averageConsultationTimeToReviewText}
                      label="Average time to file"
                      dateRange={dateRangeText}
                    />
                  </div>
                </div>

                <div className="insights-container">
                  <div className="insights-column">
                    <div className="chart-card">
                      <ChartHeader title="Consultations Ready for Review">
                        <ChartDownloadButton
                          disabled={!consultationData?.length}
                          onClick={() => downloadConsultationsReadyCSV(consultationData)}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        How the ready-for-review backlog has changed in the selected time window
                      </div>
                      <div className="chart-content">
                        <ConsultationsReadyForReviewChart data={consultationData} />
                      </div>
                    </div>

                    {showUserCharts && (
                      <div className="chart-card">
                        <ChartHeader title={`${getActionTitle(focusedAction)} Over Time`}>
                          <AggregationModeDropdown
                            mode={aggregationMode.mode}
                            isOpen={aggregationMode.overTimeOpen}
                            onToggle={() => {
                              aggregationMode.toggleOverTime();
                              focusedActionDropdown.setIsOpen(false);
                            }}
                            onSelect={aggregationMode.select}
                          />
                          <CustomDropdown
                            options={userActionOptions}
                            selectedOption={UserActionLabels[focusedAction]}
                            onSelect={handleFocusedActionChange}
                            isOpen={focusedActionDropdown.isOpen}
                            toggleDropdown={() => {
                              focusedActionDropdown.toggle([]);
                              aggregationMode.close();
                            }}
                            keyExtractor={(option, index) => `consultation-activity-time-${option.value || index}`}
                          />
                          <ChartDownloadButton
                            disabled={overTimeSeries.userNames.length === 0}
                            onClick={() => downloadUserActivityOverTimeCSV({
                              data: consultationData,
                              selectedAction: focusedAction,
                              userIdToName,
                              filteredUserIds,
                              mode: aggregationMode.mode,
                              filenamePrefix: `${chartFileSlug(getActionTitle(focusedAction))}_over_time`,
                            })}
                          />
                        </ChartHeader>
                        <div className="chart-subtitle-header">
                          Breakdown of selected activities for selected staff in selected time window, by staff
                        </div>
                        <div className="chart-content">
                          <UserActivityOverTimeChart
                            data={consultationData}
                            selectedAction={focusedAction}
                            userIdToName={userIdToName}
                            filteredUserIds={filteredUserIds}
                            aggregationMode={aggregationMode.mode}
                            activeUsers={null}
                            onToggleUser={null}
                          />
                        </div>
                      </div>
                    )}

                    {showUserCharts && (
                      <div className="chart-card">
                        <ChartHeader title={`${getActionTitle(focusedAction)} by Staff`}>
                          <CustomDropdown
                            options={userActionOptions}
                            selectedOption={UserActionLabels[focusedAction]}
                            onSelect={handleFocusedActionChange}
                            isOpen={byStaffDropdownOpen}
                            toggleDropdown={() => setByStaffDropdownOpen(!byStaffDropdownOpen)}
                            keyExtractor={(option, index) => `consultation-activity-staff-${option.value || index}`}
                          />
                        </ChartHeader>
                        <div className="chart-subtitle-header">
                          Aggregate of '{getActionTitle(focusedAction).toLowerCase()}' activity by staff in selected time window
                        </div>
                        <div className="chart-content">
                          <UserActivityByStaffChart
                            data={consultationData}
                            selectedAction={focusedAction}
                            userIdToName={userIdToName}
                            filteredUserIds={filteredUserIds}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="insights-column">
                    <div className="chart-card">
                      <ChartHeader title={`Scribe Activity (${getUserSelectionText()})`}>
                        <AggregationModeDropdown
                          mode={aggregationMode.mode}
                          isOpen={aggregationMode.activityOpen}
                          onToggle={aggregationMode.toggleActivity}
                          onSelect={aggregationMode.select}
                        />
                        <ChartDownloadButton
                          disabled={!consultationData?.length || selectedActions.length === 0}
                          onClick={() => downloadActivityOverTimeCSV({
                            data: consultationData,
                            actions: selectedActions,
                            userActionLabels: UserActionLabels,
                            filteredUserIds,
                            mode: aggregationMode.mode,
                            filenamePrefix: 'scribe_activity',
                          })}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        Breakdown of selected activities for selected staff in selected time window, by activity type
                      </div>
                      <div className="chart-content">
                        <DocumentActivityChart
                          data={consultationData}
                          allUserActions={selectedActions}
                          userActionLabels={UserActionLabels}
                          activeActions={selectedActions}
                          onToggleAction={handleToggleAction}
                          filteredUserIds={filteredUserIds}
                          aggregationMode={aggregationMode.mode}
                        />
                      </div>
                    </div>

                    <div className="chart-card">
                      <ChartHeader title="Consultation Times">
                        <CustomDropdown
                          options={consultationProcessingTimeOptions}
                          selectedOption={consultationProcessingTimeDropdown.selected.display}
                          onSelect={(option) => {
                            consultationProcessingTimeDropdown.handleSelect(option);
                          }}
                          isOpen={consultationProcessingTimeDropdown.isOpen}
                          toggleDropdown={() => consultationProcessingTimeDropdown.toggle([])}
                          keyExtractor={(option, index) => `consultation-processing-time-${option.value || index}`}
                        />
                        <ChartDownloadButton
                          disabled={processingPoints.length === 0}
                          onClick={() => downloadConsultationProcessingCSV(
                            reviewData,
                            consultationProcessingTimeDropdown.selected.value,
                            consultationProcessingTimeDropdown.selected.display,
                            consultationProcessingTimeXAxisLabels[consultationProcessingTimeDropdown.selected.value]
                          )}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        {consultationProcessingTimeSubtitles[consultationProcessingTimeDropdown.selected.value]}
                      </div>
                      <div className="chart-content">
                        <ConsultationProcessingTimesChart
                          reviewData={reviewData}
                          selectedMetric={consultationProcessingTimeDropdown.selected.value}
                          xAxisLabel={consultationProcessingTimeXAxisLabels[consultationProcessingTimeDropdown.selected.value]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
      {viewMode === 'staff' && showUserCharts && insightsType === 'consultations' && (
        <>
          <div className="staff-metrics">
            <MetricCard
              value={combinedConsultationMetrics.totalEncounters}
              label="Total encounters"
              dateRange={dateRangeText}
            />
            <MetricCard
              value={combinedConsultationMetrics.totalTime}
              label="Total time in encounters"
              dateRange={dateRangeText}
            />
            <MetricCard
              value={combinedConsultationMetrics.avgTime}
              label="Average recording duration"
              dateRange={dateRangeText}
            />
          </div>

          <div className="staff-table-container">
            <TableSectionHeader
              title="Staff Performance"
              onDownload={handleDownloadStaffSpreadsheet}
              disabled={staffSpreadsheetDisabled}
            />
            <Table
              data={consultationStaffAggregateData}
              columns={consultationStaffTableColumns}
              id="consultation-staff-insights-table"
              enableSelection={true}
              selectedRows={combinedConsultationStaffSelection}
              onSelectionChange={setSelectedConsultationStaffRows}
              getRowKey={(row) => row.userId}
              emptyMessage="No staff data available for this time period"
              sortState={consultationStaffTableSortState}
              onSortChange={setConsultationStaffTableSortState}
              filterValues={consultationStaffTableFilterValues}
              onFilterChange={setConsultationStaffTableFilterValues}
              disabledFilters={consultationStaffTableDisabledFilters}
              onDisabledFiltersChange={setConsultationStaffTableDisabledFilters}
              paginationState={consultationStaffTablePaginationState}
              onPaginationChange={setConsultationStaffTablePaginationState}
            />
          </div>

          <div className="staff-table-container">
            <TableSectionHeader
              title="Team Performance"
              onDownload={handleDownloadTeamSpreadsheet}
              disabled={teamSpreadsheetDisabled}
            />
            <Table
              data={consultationTeamAggregateData}
              columns={consultationTeamTableColumns}
              id="consultation-team-insights-table"
              enableSelection={true}
              selectedRows={selectedConsultationTeamRows}
              onSelectionChange={setSelectedConsultationTeamRows}
              getRowKey={(row) => row.teamId}
              emptyMessage="No team data available for this time period"
              sortState={consultationTeamTableSortState}
              onSortChange={setConsultationTeamTableSortState}
              filterValues={consultationTeamTableFilterValues}
              onFilterChange={setConsultationTeamTableFilterValues}
              disabledFilters={consultationTeamTableDisabledFilters}
              onDisabledFiltersChange={setConsultationTeamTableDisabledFilters}
              paginationState={consultationTeamTablePaginationState}
              onPaginationChange={setConsultationTeamTablePaginationState}
            />
          </div>
        </>
      )}
  </>
  );
};

export default InsightsConsultationsSection;
